import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * useMasterAccess — fetches the current user's access map for the Play Along
 * with the Master journey. Returns:
 *   {
 *     loading,
 *     access: { is_admin, has_master_pass, has_member_access, master_pack_instruments,
 *               owned_lesson_ids, live_credits, unlocks_all },
 *     refresh(),
 *     canDoStep2(lessonId),   // Step 1 lesson id or instrument id
 *     canDoStep3(),
 *   }
 *
 * When there is no logged-in user, everything is denied except the marketing view.
 */
export function useMasterAccess(email) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!email) {
      setAccess(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/master/access`, { params: { email } });
      setAccess(res.data);
    } catch (_) {
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { refresh(); }, [refresh]);

  const canDoStep2 = useCallback((lessonId) => {
    if (!access) return false;
    if (access.unlocks_all) return true;
    if (lessonId && (access.owned_lesson_ids || []).includes(lessonId)) return true;
    if (lessonId && (access.master_pack_instruments || []).includes(lessonId)) return true;
    return false;
  }, [access]);

  const canDoStep3 = useCallback(() => {
    if (!access) return false;
    if (access.unlocks_all) return true;
    if (access.has_master_pass) return true;
    if ((access.master_pack_instruments || []).length > 0) return true;
    if ((access.live_credits || 0) > 0) return true;
    return false;
  }, [access]);

  return { loading, access, refresh, canDoStep2, canDoStep3 };
}
